import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Users, Trophy, Timer as TimerIcon, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";

function fmtMinutes(m) {
  const hrs = Math.floor(m / 60);
  const mins = m % 60;
  if (hrs <= 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
}

export default function TeamDetail() {
  const { id } = useParams();
  const [team, setTeam] = useState(null);
  const [timers, setTimers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/teams/${id}`),
      api.get(`/teams/${id}/timers`),
      api.get(`/teams/${id}/leaderboard`),
    ])
      .then(([t, tm, lb]) => {
        if (cancelled) return;
        setTeam(t.data);
        setTimers(tm.data || []);
        setLeaderboard(lb.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not load team");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const copyCode = () => {
    if (!team) return;
    navigator.clipboard?.writeText(team.invite_code);
    toast.success(`Invite code ${team.invite_code} copied`);
  };

  return (
    <div className="min-h-screen bg-background bg-grid scanlines">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <Link to="/teams" className="inline-flex items-center font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary" data-testid="back-to-teams">
          <ArrowLeft className="mr-2 h-3.5 w-3.5" /> teams
        </Link>

        {loading && <div className="mt-8 font-mono text-sm text-muted-foreground">loading…</div>}
        {error && <div className="mt-8 font-mono text-sm text-destructive" data-testid="team-error">{error}</div>}

        {team && (
          <>
            <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="section-label mb-2">team · {team.name}</div>
                <h1 className="font-display font-bold text-4xl tracking-tight" data-testid="team-name">{team.name}</h1>
                <p className="mt-2 font-mono text-sm text-muted-foreground flex items-center gap-2">
                  <Users className="h-3.5 w-3.5" /> {team.member_count} member{team.member_count === 1 ? "" : "s"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="border border-border px-3 py-2 font-mono">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-2">invite_code</span>
                  <span className="text-primary text-sm" data-testid="team-invite-code">{team.invite_code}</span>
                </div>
                <Button variant="outline" size="icon" className="rounded-none border-border" onClick={copyCode} data-testid="team-copy-code" aria-label="Copy invite code">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Leaderboard */}
              <div className="border border-border bg-card">
                <div className="border-b border-border px-5 py-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  <Trophy className="h-3.5 w-3.5 text-primary" /> leaderboard
                </div>
                <div className="divide-y divide-border" data-testid="team-leaderboard">
                  {leaderboard.length === 0 ? (
                    <div className="p-6 font-mono text-sm text-muted-foreground">no completions yet</div>
                  ) : (
                    leaderboard.map((row, i) => (
                      <div key={row.user_id} className="flex items-center gap-4 px-5 py-4" data-testid={`leaderboard-row-${i}`}>
                        <div className="font-display font-bold text-2xl text-primary w-8">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-sm truncate">{row.user_name}</div>
                          <div className="font-mono text-xs text-muted-foreground">{row.total_cycles} cycle{row.total_cycles === 1 ? "" : "s"}</div>
                        </div>
                        <div className="font-mono text-sm text-primary">{fmtMinutes(row.total_focus_minutes)}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Members */}
              <div className="border border-border bg-card">
                <div className="border-b border-border px-5 py-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  <Users className="h-3.5 w-3.5 text-primary" /> members
                </div>
                <div className="divide-y divide-border" data-testid="team-members">
                  {(team.member_details || []).map((m) => (
                    <div key={m.id} className="px-5 py-3 flex items-center justify-between">
                      <div>
                        <div className="font-mono text-sm">{m.name}{m.id === team.owner_id ? " · owner" : ""}</div>
                        <div className="font-mono text-xs text-muted-foreground">{m.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Team timers */}
            <div className="mt-10">
              <div className="section-label mb-3">// team_timers</div>
              {timers.length === 0 ? (
                <div className="border border-dashed border-border p-10 text-center font-mono text-sm text-muted-foreground" data-testid="team-timers-empty">
                  {">"} no team timers yet. create one and tag it to this team.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border" data-testid="team-timers-list">
                  {timers.map((t) => (
                    <Link key={t.id} to={`/timer/${t.id}`} className="bg-card p-5 hover:bg-primary/5 transition-colors" data-testid={`team-timer-${t.id}`}>
                      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                        <TimerIcon className="h-3.5 w-3.5 text-primary" /> // {t.creator_name}
                      </div>
                      <div className="font-display font-bold text-lg mt-1 truncate">{t.name}</div>
                      <div className="font-mono text-xs text-muted-foreground mt-2">
                        {t.work_minutes}/{t.short_break_minutes}/{t.long_break_minutes} · cycles {t.completed_cycles}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
